import { API_ORIGIN } from '@/constants'
import { authorizedFetch, parseApiErrorMessage } from '@/lib/api'
import type { Organisation } from '@/hooks/use-organisations'

export type OrganisationPrefillMatch = {
  name: string
  domain: string
  icon_url: string | null
}

export type OrganisationPrefillSearch = {
  results: OrganisationPrefillMatch[]
  email_domain: string | null
}

export type OrganisationPrefillSocialField =
  | 'linkedin_url'
  | 'x_url'
  | 'instagram_url'
  | 'facebook_url'
  | 'youtube_url'
  | 'github_url'
  | 'tiktok_url'

export type OrganisationPrefillDraft = {
  domain: string
  website: string
  fetched: boolean
  description: string
  industry: string
  company_size: string
  founded_year: number | null
  city: string
  country: string
  socials: Partial<Record<OrganisationPrefillSocialField, string>>
  logo_preview_url: string | null
}

/** Company matches for a name; an empty query returns matches for the user's work email domain. */
export async function searchOrganisationMatches(
  query: string,
  signal?: AbortSignal,
): Promise<OrganisationPrefillSearch> {
  const params = new URLSearchParams()
  const trimmed = query.trim()
  if (trimmed) params.set('q', trimmed)
  const qs = params.toString()
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/organisations/prefill/search${qs ? `?${qs}` : ''}`,
    { signal },
  )
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Company lookup failed'))
  }
  return (await response.json()) as OrganisationPrefillSearch
}

/** Suggested company details read from the organisation's homepage. */
export async function draftOrganisationDetails(
  domain: string,
  signal?: AbortSignal,
): Promise<OrganisationPrefillDraft> {
  const response = await authorizedFetch(`${API_ORIGIN}/api/organisations/prefill/draft`, {
    method: 'POST',
    body: JSON.stringify({ domain }),
    signal,
  })
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Could not read that website'))
  }
  return (await response.json()) as OrganisationPrefillDraft
}

export async function importOrganisationLogo(slug: string, domain: string): Promise<Organisation> {
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/logo/import/`,
    {
      method: 'POST',
      body: JSON.stringify({ domain }),
    },
  )
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Could not import that logo'))
  }
  return (await response.json()) as Organisation
}

/** Bare host from a saved website (`https://www.acme.com/about` → `acme.com`). */
export function websiteDomain(website: string | null | undefined): string | null {
  const trimmed = (website ?? '').trim()
  if (!trimmed) return null
  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`)
    const host = url.hostname.toLowerCase().replace(/^www\./, '')
    return host.includes('.') ? host : null
  } catch {
    return null
  }
}
